<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';

import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Badge from 'dashboard/components-next/badge/Badge.vue';

import { getDeudaTotalConFacturasJson, getDeudaJson } from 'dashboard/api/cea';
import { logCeaContractApi } from 'dashboard/routes/dashboard/contracts/helpers/ceaContractApiDebug';

useI18n();
const router = useRouter();
const route = useRoute();

// State
const deudaData = ref(null);
const resultado = ref(null);
const isLoading = ref(true);
const error = ref(null);

// Computed
const contratoId = computed(() => route.params.contratoId);
const explotacion = computed(() => route.params.explotacion);

const deudaTotal = computed(() =>
  parseFloat(deudaData.value?.deudaTotal || '0')
);
const deudaPrincipal = computed(() =>
  parseFloat(deudaData.value?.deuda || '0')
);
const deudaComision = computed(() =>
  parseFloat(deudaData.value?.deudaComision || '0')
);
const saldoTotal = computed(() =>
  parseFloat(deudaData.value?.saldoAnteriorTotal || '0')
);
const tieneDeuda = computed(() => deudaTotal.value > 0);
const formatCurrency = value => `$${value.toFixed(2)}`;

// Methods
const fetchDeuda = async () => {
  if (!contratoId.value || !explotacion.value) {
    error.value = 'Faltan parámetros requeridos';
    isLoading.value = false;
    return;
  }

  isLoading.value = true;
  error.value = null;

  const explotacionFormatted = explotacion.value.padStart(2, '0');

  try {
    // Try new endpoint first (resilient to billing cycle state)
    const data = await getDeudaTotalConFacturasJson(
      contratoId.value,
      explotacionFormatted,
      'es'
    );
    logCeaContractApi('getDeudaTotalConFacturasJson', data, {
      source: 'Deuda.vue',
      contratoId: contratoId.value,
      explotacion: explotacionFormatted,
    });

    const deuda = data?.deudaTotalFacturas || data?.deuda || data;
    if (!deuda || deuda.deudaTotal === undefined) {
      throw new Error('Respuesta vacía de getDeudaTotalConFacturas');
    }

    deudaData.value = deuda;
    resultado.value = data?.resultado || data?.reultado;
  } catch {
    // Fallback to old endpoint
    try {
      const data = await getDeudaJson(
        'CONTRATO',
        contratoId.value,
        explotacionFormatted,
        'es'
      );
      logCeaContractApi('getDeudaJson (fallback)', data, {
        source: 'Deuda.vue',
        contratoId: contratoId.value,
        explotacion: explotacionFormatted,
      });

      const deuda = data?.deuda || data;
      if (!deuda) {
        throw new Error(
          'No se encontró información de deuda para este contrato'
        );
      }

      deudaData.value = deuda;
      resultado.value = data?.reultado || data?.resultado;
    } catch (oldErr) {
      error.value = `Error al cargar deuda: ${oldErr.message || 'Error desconocido'}`;
      useAlert(error.value);
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  fetchDeuda();
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 bg-n-background">
    <!-- Loading State -->
    <div v-if="isLoading" class="flex items-center justify-center py-12">
      <div class="text-center">
        <Spinner :size="32" />
        <h2 class="text-xl font-semibold mt-4">
          {{ $t('CONTRACTS.DEUDA.LOADING') }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-2">
          {{ $t('CONTRACTS.DEUDA.LOADING_DESC', { id: contratoId }) }}
        </p>
      </div>
    </div>

    <!-- Error State -->
    <div
      v-else-if="error || !deudaData"
      class="flex items-center justify-center py-12 px-4"
    >
      <div class="text-center max-w-xl">
        <span class="i-lucide-alert-circle text-6xl text-n-ruby-11 mb-4" />
        <h2 class="text-xl font-semibold px-8">
          {{ error || $t('CONTRACTS.DEUDA.NOT_FOUND') }}
        </h2>
        <p class="text-sm text-n-slate-11 mt-3 px-12">
          {{ error || $t('CONTRACTS.DEUDA.NOT_FOUND_DESC') }}
        </p>
        <div class="flex gap-2 justify-center mt-6">
          <Button color="blue" @click="router.back()">
            {{ $t('CONTRACTS.BACK') }}
          </Button>
        </div>
      </div>
    </div>

    <!-- Deuda Content -->
    <div v-else class="flex flex-col h-full overflow-auto">
      <!-- Header -->
      <header class="flex items-center gap-4 p-4 border-b border-n-weak">
        <Button color="slate" size="small" @click="router.back()">
          <span class="i-lucide-arrow-left" />
        </Button>
        <div class="flex-1">
          <h1 class="text-2xl font-semibold text-n-slate-12">
            {{ $t('CONTRACTS.DEUDA.ACCOUNT_STATUS') }}
          </h1>
          <!-- eslint-disable-next-line vue/no-bare-strings-in-template -->
          <p class="text-sm text-n-slate-11 mt-1">
            {{ $t('CONTRACTS.COMMON.CONTRACT_PREFIX') }} {{ contratoId }} |
            {{ $t('CONTRACTS.COMMON.EXPLOITATION_PREFIX') }} {{ explotacion }}
          </p>
        </div>
        <Badge
          v-if="resultado && resultado.codigoError === '0'"
          color="green"
          size="small"
          class="gap-1"
        >
          <span class="i-lucide-check-circle" />
          {{ $t('CONTRACTS.DEUDA.QUERY_SUCCESS') }}
        </Badge>
      </header>

      <!-- Content -->
      <div class="flex-1 overflow-auto p-4 space-y-6">
        <!-- Estado de Deuda Alert -->
        <div
          class="p-4 rounded-lg border"
          :class="
            tieneDeuda
              ? 'bg-n-ruby-3 border-n-ruby-6 text-n-ruby-11'
              : 'bg-green-50 border-green-200 text-green-900'
          "
        >
          <div class="flex items-center gap-2">
            <span
              :class="
                tieneDeuda ? 'i-lucide-alert-circle' : 'i-lucide-check-circle'
              "
            />
            <p class="text-sm font-medium">
              <span v-if="tieneDeuda">
                {{ $t('CONTRACTS.DEUDA.HAS_DEBT') }}
                <strong>{{ formatCurrency(deudaTotal) }}</strong>
              </span>
              <span v-else>{{ $t('CONTRACTS.DEUDA.NO_DEBT') }}</span>
            </p>
          </div>
        </div>

        <!-- Información del Cliente -->
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <h3
            class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-user" />
            {{ $t('CONTRACTS.COMMON.CLIENT_INFO') }}
          </h3>
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('CONTRACTS.COMMON.CLIENT_NAME') }}
              </p>
              <p class="text-sm font-medium text-n-slate-12">
                {{ deudaData.nombreCliente }}
              </p>
            </div>
            <div>
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('CONTRACTS.COMMON.EXPLOITATION_LABEL') }}
              </p>
              <Badge color="slate" size="small" class="font-mono">
                {{ deudaData.explotacion }}
              </Badge>
            </div>
            <div class="md:col-span-2">
              <p class="text-xs text-n-slate-11 mb-1">
                {{ $t('CONTRACTS.COMMON.SUPPLY_ADDRESS') }}
              </p>
              <div class="flex items-start gap-2">
                <span class="i-lucide-map-pin text-n-slate-11 mt-0.5" />
                <p class="text-sm font-medium text-n-slate-12">
                  {{ deudaData.direccion }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Deuda Actual y Saldo Anterior -->
        <div class="grid gap-4 md:grid-cols-2">
          <!-- Deuda Actual -->
          <div class="bg-n-background border border-n-weak rounded-lg p-4">
            <h3
              class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
            >
              <span class="i-lucide-dollar-sign" />
              {{ $t('CONTRACTS.COMMON.CURRENT_DEBT') }}
            </h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center pb-2">
                <span class="text-xs text-n-slate-11">{{
                  $t('CONTRACTS.COMMON.MAIN_DEBT')
                }}</span>
                <span
                  class="text-sm font-semibold"
                  :class="
                    deudaPrincipal > 0 ? 'text-n-ruby-11' : 'text-green-600'
                  "
                >
                  {{ formatCurrency(deudaPrincipal) }}
                </span>
              </div>
              <div class="border-t border-n-weak" />
              <div class="flex justify-between items-center pb-2">
                <span class="text-xs text-n-slate-11">{{
                  $t('CONTRACTS.COMMON.COMMISSIONS')
                }}</span>
                <span
                  class="text-sm font-semibold"
                  :class="
                    deudaComision > 0 ? 'text-n-ruby-11' : 'text-green-600'
                  "
                >
                  {{ formatCurrency(deudaComision) }}
                </span>
              </div>
              <div class="border-t border-n-weak" />
              <div
                class="flex justify-between items-center pt-2 bg-n-alpha-1 p-3 rounded-lg"
              >
                <span class="text-sm font-semibold">{{
                  $t('CONTRACTS.COMMON.TOTAL_DEBT_LABEL')
                }}</span>
                <span
                  class="text-xl font-bold"
                  :class="deudaTotal > 0 ? 'text-n-ruby-11' : 'text-green-600'"
                >
                  {{ formatCurrency(deudaTotal) }}
                </span>
              </div>
              <div
                class="grid grid-cols-2 gap-4 text-center pt-2 border-t border-n-weak"
              >
                <div>
                  <p class="text-xs text-n-slate-11">
                    {{ $t('CONTRACTS.COMMON.PREVIOUS_CYCLES') }}
                  </p>
                  <p class="text-lg font-bold text-n-slate-12">
                    {{ deudaData.ciclosAnteriores }}
                  </p>
                </div>
                <div>
                  <p class="text-xs text-n-slate-11">
                    {{ $t('CONTRACTS.COMMON.TOTAL_CYCLES') }}
                  </p>
                  <p class="text-lg font-bold text-n-slate-12">
                    {{ deudaData.ciclosTotales }}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <!-- Saldo Anterior -->
          <div class="bg-n-background border border-n-weak rounded-lg p-4">
            <h3
              class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
            >
              <span class="i-lucide-calendar" />
              {{ $t('CONTRACTS.COMMON.PREVIOUS_BALANCE') }}
            </h3>
            <div class="space-y-3">
              <div class="flex justify-between items-center pb-2">
                <span class="text-xs text-n-slate-11">{{
                  $t('CONTRACTS.COMMON.MAIN_BALANCE')
                }}</span>
                <span class="text-sm font-semibold text-n-slate-12">
                  {{
                    formatCurrency(parseFloat(deudaData.saldoAnterior || '0'))
                  }}
                </span>
              </div>
              <div class="border-t border-n-weak" />
              <div class="flex justify-between items-center pb-2">
                <span class="text-xs text-n-slate-11">{{
                  $t('CONTRACTS.COMMON.COMMISSIONS')
                }}</span>
                <span class="text-sm font-semibold text-n-slate-12">
                  {{
                    formatCurrency(
                      parseFloat(deudaData.saldoAnteriorComision || '0')
                    )
                  }}
                </span>
              </div>
              <div class="border-t border-n-weak" />
              <div
                class="flex justify-between items-center pt-2 bg-n-alpha-1 p-3 rounded-lg"
              >
                <span class="text-sm font-semibold">{{
                  $t('CONTRACTS.COMMON.TOTAL_PREVIOUS_BALANCE')
                }}</span>
                <span class="text-xl font-bold text-n-slate-12">
                  {{ formatCurrency(saldoTotal) }}
                </span>
              </div>
            </div>
          </div>
        </div>

        <!-- Documentos de Pago -->
        <div class="bg-n-background border border-n-weak rounded-lg p-4">
          <h3
            class="text-lg font-semibold text-n-slate-12 mb-4 flex items-center gap-2"
          >
            <span class="i-lucide-file-text" />
            {{ $t('CONTRACTS.COMMON.PAYMENT_DOCUMENTS') }}
          </h3>
          <div class="grid gap-4 md:grid-cols-2">
            <div>
              <p class="text-xs text-n-slate-11 mb-2">
                {{ $t('CONTRACTS.COMMON.CURRENT_PAYMENT_DOC') }}
              </p>
              <div
                v-if="deudaData.documentoPago"
                class="flex items-center gap-2 p-3 bg-n-alpha-1 rounded-lg"
              >
                <span class="i-lucide-file-text text-n-brand" />
                <span class="text-sm font-medium font-mono text-n-slate-12">{{
                  deudaData.documentoPago
                }}</span>
              </div>
              <p
                v-else
                class="text-xs text-n-slate-10 italic p-3 bg-n-solid-3 rounded-lg"
              >
                {{ $t('CONTRACTS.COMMON.NOT_AVAILABLE') }}
              </p>
            </div>
            <div>
              <p class="text-xs text-n-slate-11 mb-2">
                {{ $t('CONTRACTS.COMMON.PREVIOUS_PAYMENT_DOC') }}
              </p>
              <div
                v-if="deudaData.documentoPagoAnterior"
                class="flex items-center gap-2 p-3 bg-n-alpha-1 rounded-lg"
              >
                <span class="i-lucide-file-text text-n-slate-11" />
                <span class="text-sm font-medium font-mono text-n-slate-12">{{
                  deudaData.documentoPagoAnterior
                }}</span>
              </div>
              <p
                v-else
                class="text-xs text-n-slate-10 italic p-3 bg-n-solid-3 rounded-lg"
              >
                {{ $t('CONTRACTS.COMMON.NOT_AVAILABLE') }}
              </p>
            </div>
          </div>
        </div>

        <!-- Acciones -->
        <div
          v-if="tieneDeuda"
          class="bg-n-background border border-n-weak rounded-lg p-4"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
            {{ $t('CONTRACTS.DEUDA.RECOMMENDED_ACTIONS') }}
          </h3>
          <div class="space-y-2">
            <div class="p-3 bg-n-alpha-1 rounded-lg">
              <div class="flex items-center gap-2">
                <span class="i-lucide-trending-down text-n-ruby-11" />
                <p class="text-sm text-n-slate-11">
                  {{ $t('CONTRACTS.DEUDA.PAYMENT_RECOMMENDATION') }}
                </p>
              </div>
            </div>
            <div class="flex gap-2">
              <Button color="blue" class="flex-1">
                <span class="i-lucide-dollar-sign mr-2" />
                {{ $t('CONTRACTS.DEUDA.GENERATE_RECEIPT') }}
              </Button>
              <Button
                color="slate"
                class="flex-1"
                @click="
                  router.push({
                    name: 'contract_detail',
                    params: {
                      accountId: route.params.accountId,
                      contratoId: contratoId,
                    },
                  })
                "
              >
                <span class="i-lucide-file-text mr-2" />
                {{ $t('CONTRACTS.DEUDA.VIEW_CONTRACT_DETAILS') }}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
